import React, { type Context, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { usePreference, setPreference } from '@fs/zion-preferences'
import updateObjectPreference from './updateObjectPreference'


export type ProviderValue<T> = [T, (T) => void]

type PreferenceProviderProps<T> = {
  children: ReactNode
  Context: Context<ProviderValue<T>>
  loadingComponent: ReactNode
  preferenceKey: string
}

/**
 * This wrapping component lets us gate the actual provider from being rendered until we fetch
 * the preference. This way we don't have to manage a bunch of "could be undefined" scenarios for
 * the provider value.
 */
export default function PreferenceProvider<T>(props: PreferenceProviderProps<T> & { defaultValue: T }): JSX.Element {
  const { preferenceKey, defaultValue, loadingComponent } = props
  const preferenceValue = usePreference<T>(preferenceKey)

  // In the case the user's preference isn't set, update it to the default preference.
  useEffect(() => {
    if (preferenceValue === '') {
      setPreference(preferenceKey, defaultValue)
    }

    updateObjectPreference(defaultValue, preferenceValue, preferenceKey)
  }, [defaultValue, preferenceKey, preferenceValue])

  const gatingValue = preferenceValue === '' ? defaultValue : preferenceValue

  return gatingValue == null ? (
    <>{loadingComponent}</>
  ) : (
    <InternalPreferenceProvider {...props} preferenceValue={gatingValue} />
  )
}

function InternalPreferenceProvider<T>({
  preferenceKey,
  preferenceValue,
  Context,
  children,
}: Omit<PreferenceProviderProps<T>, 'loadingComponent'> & { preferenceValue: T }): JSX.Element {
  const [pref, setPref] = useState<T>(preferenceValue)

  const updatePref = useCallback(
    (newValue: T): void => {
      setPref(newValue)
      setPreference(preferenceKey, newValue)
    },
    [preferenceKey]
  )

  const value: ProviderValue<T> = useMemo(() => [pref, updatePref], [pref, updatePref])

  return <Context.Provider value={value}>{children}</Context.Provider>
}
